/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiBadge, EuiCallOut, EuiFlexGroup, EuiSpacer, EuiText } from '@elastic/eui'

import type { TestRoleMappingsResponse } from '@modules/cloud-api/v1/types'
import { FormattedMessage } from '@modules/i18n'
import { isOrgOwnerRoleAssignments, isRoleAssignmentsEmpty } from '@modules/role-assignments-lib'

import RoleBadges from '@/components/Organization/RoleBadges'

import { messages } from './message'

interface Props {
  testResult?: TestRoleMappingsResponse
  organizationId: string
  type: 'Update' | 'Delete'
  roleMappingName: string
}

const VerificationResult = ({ testResult, organizationId, type, roleMappingName }: Props) => {
  const hasOrgOwnerRole = isOrgOwnerRoleAssignments({
    roleAssignments: testResult?.role_assignments,
    organizationId,
  })

  return (
    <div>
      <EuiCallOut
        color={hasOrgOwnerRole ? 'success' : 'danger'}
        title={
          <FormattedMessage
            {...messages[`${hasOrgOwnerRole ? 'success' : 'failed'}${type}Title`]}
            values={{
              roleMappingName: <b>{roleMappingName}</b>,
            }}
          />
        }
        iconType={hasOrgOwnerRole ? 'check' : 'cross'}
      >
        <EuiText size='s'>
          <p>
            <FormattedMessage
              {...messages[`${hasOrgOwnerRole ? 'success' : 'failed'}${type}Description`]}
              values={{
                roleMappingName: <b>{roleMappingName}</b>,
              }}
            />
          </p>
          <p>
            <EuiFlexGroup grow='false' gutterSize='s'>
              <FormattedMessage {...messages.mappingRoles} />
              {isRoleAssignmentsEmpty(testResult?.role_assignments) ? (
                <FormattedMessage {...messages.none} />
              ) : (
                <RoleBadges
                  roleAssignments={testResult?.role_assignments}
                  organizationId={organizationId}
                />
              )}
            </EuiFlexGroup>
          </p>
          <p>
            <FormattedMessage {...messages.verifyDetails} />
            <ul>
              <li>
                <FormattedMessage {...messages.email} values={{ email: testResult?.email }} />
              </li>
              <li>
                <FormattedMessage
                  {...messages.idpGroup}
                  values={{
                    idpGroups: <IdpGroups groups={testResult?.groups} />,
                  }}
                />
              </li>
              <li>
                <FormattedMessage
                  {...messages.name}
                  values={{
                    name: `${testResult?.first_name || ''} ${testResult?.last_name || ''}`.trim() || (
                      <FormattedMessage {...messages.none} />
                    ),
                  }}
                />
              </li>
            </ul>
          </p>
        </EuiText>
      </EuiCallOut>
      <EuiSpacer />
    </div>
  )
}

const IdpGroups = ({ groups }: { groups: string[] | undefined }) => {
  if (!groups?.length) {
    return <FormattedMessage {...messages.none} />
  }

  return (
    <React.Fragment>
      {groups.map((groupName) => (
        <EuiBadge color='hollow' key={groupName}>
          {groupName}
        </EuiBadge>
      ))}
    </React.Fragment>
  )
}

export default VerificationResult
